"use strict";
/**
 * Community guideline catalog: rule labels, parent-category groupings, and
 * lookup helpers used by moderation UI and AI prompts.
 *
 * Static data: a flat `GUIDELINES` map of leaf-rule labels, a
 * `PARENT_GUIDELINES` map of category labels, and a `communityGuidelines` array
 * that nests the leaves under their parents in display order. Each leaf is
 * identified by its stable `ModerationRule` key; the numeric rule IDs the
 * moderation prompts emit stay server-side, and `AI_CITABLE_RULES` in
 * `./community-guidelines` says which rules a prompt can cite.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.communityGuidelines = exports.PARENT_GUIDELINES = exports.GUIDELINES = void 0;
exports.groupGuidelinesByParent = groupGuidelinesByParent;
exports.getRuleLabelByKey = getRuleLabelByKey;
exports.getParentRuleByChildKey = getParentRuleByChildKey;
const label_1 = require("./translations/label");
exports.GUIDELINES = {
    NO_UNCIVIL: (0, label_1.t)('community_guidelines.rule_express_civility', 'Express your ideas with civility'),
    NO_HARM: (0, label_1.t)('community_guidelines.rule_no_violent_content', 'No violent content or calls for harm'),
    NO_INSULTS: (0, label_1.t)('community_guidelines.rule_avoid_sarcasm', 'Avoid sarcasm and dismissive responses'),
    NO_LABELS: (0, label_1.t)('community_guidelines.rule_avoid_derogatory_labels', 'Avoid derogatory labels'),
    BACKUP_CLAIMS: (0, label_1.t)('community_guidelines.rule_provide_sources', 'Provide sources for factual claims'),
    NO_SPAM: (0, label_1.t)('community_guidelines.rule_no_spam', 'No spam or commercial promotion'),
    RESPECT_PRIVACY: (0, label_1.t)('community_guidelines.rule_respect_privacy', 'Respect the privacy of others, no personal information sharing'),
    OFF_TOPIC: (0, label_1.t)('community_guidelines.rule_no_offtopic', 'No off-topic posts'),
    NO_ILLEGAL: (0, label_1.t)('community_guidelines.rule_no_illegal_content', 'No illegal content'),
    NO_PERSONAL_ATTACKS: (0, label_1.t)('community_guidelines.rule_focus_on_ideas', 'Focus on ideas, rather than character judgments'),
    BE_CONSTRUCTIVE: (0, label_1.t)('community_guidelines.rule_explain_constructively', 'Explain disagreements constructively'),
};
exports.PARENT_GUIDELINES = {
    ENGAGE_RESPECTFULLY: (0, label_1.t)('community_guidelines.category_engage_respectfully', 'Engage Respectfully'),
    SUPPORT_YOUR_CLAIMS: (0, label_1.t)('community_guidelines.category_support_claims', 'Support Your Claims'),
    NO_HARMFUL_CONTENT: (0, label_1.t)('community_guidelines.category_no_harmful_content', 'No Harmful Content'),
    NO_DISRUPTIVE_BEHAVIOR: (0, label_1.t)('community_guidelines.category_no_disruptive_behavior', 'No Disruptive Behavior'),
};
exports.communityGuidelines = [
    {
        parent: exports.PARENT_GUIDELINES.ENGAGE_RESPECTFULLY,
        children: [
            { key: 'NO_UNCIVIL', label: exports.GUIDELINES.NO_UNCIVIL },
            { key: 'NO_PERSONAL_ATTACKS', label: exports.GUIDELINES.NO_PERSONAL_ATTACKS },
            { key: 'NO_LABELS', label: exports.GUIDELINES.NO_LABELS },
            { key: 'NO_INSULTS', label: exports.GUIDELINES.NO_INSULTS },
            { key: 'BE_CONSTRUCTIVE', label: exports.GUIDELINES.BE_CONSTRUCTIVE },
        ],
    },
    {
        parent: exports.PARENT_GUIDELINES.SUPPORT_YOUR_CLAIMS,
        children: [{ key: 'BACKUP_CLAIMS', label: exports.GUIDELINES.BACKUP_CLAIMS }],
    },
    {
        parent: exports.PARENT_GUIDELINES.NO_HARMFUL_CONTENT,
        children: [
            { key: 'NO_HARM', label: exports.GUIDELINES.NO_HARM },
            { key: 'RESPECT_PRIVACY', label: exports.GUIDELINES.RESPECT_PRIVACY },
            { key: 'NO_ILLEGAL', label: exports.GUIDELINES.NO_ILLEGAL }, // we don't currently AI-moderate this
        ],
    },
    {
        parent: exports.PARENT_GUIDELINES.NO_DISRUPTIVE_BEHAVIOR,
        children: [
            { key: 'NO_SPAM', label: exports.GUIDELINES.NO_SPAM },
            { key: 'OFF_TOPIC', label: exports.GUIDELINES.OFF_TOPIC },
        ],
    },
];
/**
 * Groups rules under their parent guideline, in `communityGuidelines` order,
 * dropping duplicates and empty groups.
 */
function groupGuidelinesByParent(violationKeys) {
    const deduplicatedKeys = new Set(violationKeys);
    return exports.communityGuidelines
        .map((group) => {
        return {
            parent: group.parent,
            children: group.children.filter((child) => deduplicatedKeys.has(child.key)).map((child) => child.label),
        };
    })
        .filter((group) => group.children.length > 0);
}
/** Looks up a recorded `ModerationRule` violation's translatable label. */
function getRuleLabelByKey(key) {
    return exports.GUIDELINES[key];
}
/** Looks up the parent category label for a recorded `ModerationRule`. */
function getParentRuleByChildKey(key) {
    for (const group of exports.communityGuidelines) {
        const childRule = group.children.find((child) => child.key === key);
        if (childRule) {
            return group.parent;
        }
    }
    return null;
}
//# sourceMappingURL=community-guideline-labels.js.map